import type { TutorialStep, TutorialStepId } from './tutorialProgress';
import { TUTORIAL_STEPS } from './tutorialProgress';

type TutorialOverlayProps = {
  completedStepIds: TutorialStepId[];
  currentStep: TutorialStep;
  align: 'left' | 'right';
  onDismiss: () => void;
};

function frameClip() {
  return 'polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px))';
}

export function TutorialOverlay({ completedStepIds, currentStep, align, onDismiss }: TutorialOverlayProps) {
  const currentIndex = TUTORIAL_STEPS.findIndex((step) => step.id === currentStep.id);
  const visibleIndex = currentIndex >= 0 ? currentIndex + 1 : completedStepIds.length + 1;
  const progressPercent = Math.round((completedStepIds.length / TUTORIAL_STEPS.length) * 100);

  return (
    <section
      role="status"
      aria-live="polite"
      aria-label="Training tutorial"
      style={{
        width: 'min(286px, calc(100vw - 28px))',
        padding: '12px 13px',
        color: '#edf7ff',
        background: 'linear-gradient(135deg, rgba(4,10,18,0.92), rgba(8,22,38,0.84))',
        border: '1px solid rgba(98,221,255,0.34)',
        boxShadow: '0 0 24px rgba(98,221,255,0.13), inset 0 0 20px rgba(98,221,255,0.05)',
        clipPath: frameClip(),
        WebkitClipPath: frameClip(),
        fontFamily: 'system-ui, sans-serif',
        textAlign: align === 'right' ? 'right' : 'left',
        pointerEvents: 'auto',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexDirection: align === 'right' ? 'row-reverse' : 'row' }}>
        <div style={{ display: 'grid', gap: 2 }}>
          <span style={{ fontSize: 9.5, letterSpacing: 2, textTransform: 'uppercase', color: '#62ddff', fontWeight: 800 }}>
            Training
          </span>
          <span style={{ fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', color: 'rgba(231,244,252,0.58)' }}>
            {visibleIndex} / {TUTORIAL_STEPS.length}
          </span>
        </div>
        <button
          type="button"
          aria-label="Dismiss training tutorial"
          onClick={onDismiss}
          style={{
            padding: '6px 8px',
            color: '#9fb4c7',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.12)',
            outlineColor: '#62ddff',
            cursor: 'pointer',
            clipPath: frameClip(),
            WebkitClipPath: frameClip(),
            fontSize: 9,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
          }}
        >
          Skip
        </button>
      </div>

      <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
        <div style={{ display: 'grid', gap: 4 }}>
          <strong style={{ color: '#f4fbff', fontSize: 15, letterSpacing: 1.4, textTransform: 'uppercase' }}>
            {currentStep.title}
          </strong>
          <span style={{ color: 'rgba(231,244,252,0.78)', fontSize: 11.5, lineHeight: 1.35 }}>
            {currentStep.description}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : 'flex-start', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ color: 'rgba(231,244,252,0.58)', fontSize: 9, letterSpacing: 1.2, textTransform: 'uppercase' }}>Hint</span>
          <span
            style={{
              padding: '4px 8px',
              color: '#f4fbff',
              background: 'rgba(98,221,255,0.10)',
              border: '1px solid rgba(98,221,255,0.24)',
              clipPath: frameClip(),
              WebkitClipPath: frameClip(),
              fontSize: 10,
              letterSpacing: 1.3,
              textTransform: 'uppercase',
            }}
          >
            {currentStep.keyHint}
          </span>
        </div>

        <div aria-hidden="true" style={{ height: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
          <div style={{ width: `${progressPercent}%`, height: '100%', background: 'linear-gradient(90deg, #62ddff, #8ef0c9)' }} />
        </div>
      </div>
    </section>
  );
}
